import { ActionIcon, Drawer } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { PanelLeft } from 'lucide-react'
import { createContext, useContext, type ReactNode } from 'react'

type SidebarHelpers = { closeSidebar: () => void }
type SidebarProp = ReactNode | ((helpers: SidebarHelpers) => ReactNode)

const SidebarCloseContext = createContext<() => void>(() => {})

/** Call from any component rendered inside the sidebar to close the mobile drawer. No-op on desktop. */
export function useSidebarClose() {
  return useContext(SidebarCloseContext)
}

export default function SidebarLayout({
  sidebar,
  sidebarWidth = 260,
  children,
}: {
  sidebar: SidebarProp
  sidebarWidth?: number
  children: ReactNode
}) {
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false)
  const isMobile = useMediaQuery('(max-width: 48em)') ?? false

  const sidebarContent = typeof sidebar === 'function' ? sidebar({ closeSidebar: closeDrawer }) : sidebar

  return (
    <SidebarCloseContext.Provider value={closeDrawer}>
      <div style={{ display: 'flex', height: 'calc(100vh - var(--app-shell-header-height, 60px))' }}>
        {/* Desktop: inline sidebar */}
        {!isMobile && (
          <div
            style={{
              width: sidebarWidth,
              flexShrink: 0,
              borderRight: '1px solid var(--mantine-color-gray-3)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {sidebarContent}
          </div>
        )}

        {/* Mobile: drawer sidebar */}
        {isMobile && (
          <Drawer
            opened={drawerOpened}
            onClose={closeDrawer}
            size={sidebarWidth}
            padding={0}
            withCloseButton={false}
            position="left"
            styles={{
              inner: { top: 'var(--app-shell-header-height, 60px)' },
              content: { height: 'calc(100vh - var(--app-shell-header-height, 60px))' },
              overlay: { top: 'var(--app-shell-header-height, 60px)' },
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {sidebarContent}
            </div>
          </Drawer>
        )}

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Mobile: toggle button to open sidebar drawer */}
          {isMobile && (
            <div
              style={{
                padding: '6px 12px',
                borderBottom: '1px solid var(--mantine-color-gray-3)',
                flexShrink: 0,
              }}
            >
              <ActionIcon variant="subtle" size="sm" onClick={openDrawer} aria-label="Open sidebar">
                <PanelLeft size={18} />
              </ActionIcon>
            </div>
          )}
          {children}
        </div>
      </div>
    </SidebarCloseContext.Provider>
  )
}
